import { Combobox } from '@ntconsult/aluma-react';
import { useTranslation } from 'react-i18next';
import type { SalesProfileOption } from '../types/SalesProfile';
import { formatMoney } from '../utils/money';

/** Opção explícita de "sem perfil": no legado o perfil de venda não é obrigatório. */
const NO_PROFILE = 'none';

type SalesProfileSelectProps = {
  profiles: SalesProfileOption[];
  value: number | null;
  /** Rótulo do perfil já gravado, que pode não estar entre os ativos (versão antiga do contrato). */
  currentLabel?: string;
  onChange: (profile: SalesProfileOption | null) => void;
  disabled?: boolean;
  invalid?: boolean;
  /** Injetado pelo `FormField`. */
  id?: string;
};

/** Perfis ativos do contrato (cabem numa página), com o rótulo do legado: versão, nome e preço. */
export function SalesProfileSelect({
  profiles,
  value,
  currentLabel,
  onChange,
  disabled,
  invalid,
  id,
}: SalesProfileSelectProps) {
  const { t, i18n } = useTranslation('alocacao');
  const labelOf = (profile: SalesProfileOption) =>
    t('form.perfilVendaOpcao', {
      versao: profile.version,
      nome: profile.name,
      preco: formatMoney(profile.hourlyRate, profile.currency.iso, i18n.language),
    });

  return (
    <Combobox
      id={id}
      value={value === null ? NO_PROFILE : String(value)}
      onValueChange={(next) =>
        onChange(profiles.find((profile) => String(profile.id) === next) ?? null)
      }
      placeholder={t('form.perfilVendaPlaceholder')}
      disabled={disabled}
      invalid={invalid}
      valueLabels={{
        [NO_PROFILE]: t('form.perfilVendaNenhum'),
        ...(value !== null && currentLabel ? { [String(value)]: currentLabel } : {}),
        ...Object.fromEntries(profiles.map((profile) => [String(profile.id), labelOf(profile)])),
      }}
    >
      <Combobox.Trigger />
      <Combobox.Content searchPlaceholder={t('form.perfilVendaBuscar')}>
        <Combobox.Option value={NO_PROFILE}>{t('form.perfilVendaNenhum')}</Combobox.Option>
        {profiles.map((profile) => (
          <Combobox.Option key={profile.id} value={String(profile.id)}>
            {labelOf(profile)}
          </Combobox.Option>
        ))}
      </Combobox.Content>
    </Combobox>
  );
}
